import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

/**
 * Generic CRUD slice factory
 * Creates a Redux slice with standard CRUD operations
 */
export const createCrudSlice = (name, api) => {
  // Async thunks
  const fetchAll = createAsyncThunk(
    `${name}/fetchAll`,
    async (params = {}, { rejectWithValue }) => {
      try {
        const response = await api.getAll(params);
        return response;
      } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
      }
    }
  );

  const fetchById = createAsyncThunk(
    `${name}/fetchById`,
    async (id, { rejectWithValue }) => {
      try {
        const response = await api.getById(id);
        return response;
      } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
      }
    }
  );

  const create = createAsyncThunk(
    `${name}/create`,
    async (data, { rejectWithValue }) => {
      try {
        const response = await api.create(data);
        return response;
      } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
      }
    }
  );

  const update = createAsyncThunk(
    `${name}/update`,
    async ({ id, data }, { rejectWithValue }) => {
      try {
        const response = await api.update(id, data);
        return response;
      } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
      }
    }
  );

  const remove = createAsyncThunk(
    `${name}/delete`,
    async (id, { rejectWithValue }) => {
      try {
        await api.delete(id);
        return id;
      } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
      }
    }
  );

  const slice = createSlice({
    name,
    initialState: {
      items: [],
      currentItem: null,
      pagination: {
        page: 1,
        limit: 50,
        total: 0,
        pages: 0,
      },
      loading: false,
      error: null,
    },
    reducers: {
      clearError: (state) => {
        state.error = null;
      },
      clearCurrentItem: (state) => {
        state.currentItem = null;
      },
    },
    extraReducers: (builder) => {
      builder
        // Fetch all
        .addCase(fetchAll.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(fetchAll.fulfilled, (state, action) => {
          state.loading = false;
          state.items = action.payload.items || action.payload;
          if (action.payload.pagination) {
            state.pagination = action.payload.pagination;
          }
        })
        .addCase(fetchAll.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        })
        // Fetch by ID
        .addCase(fetchById.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(fetchById.fulfilled, (state, action) => {
          state.loading = false;
          state.currentItem = action.payload;
        })
        .addCase(fetchById.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        })
        // Create
        .addCase(create.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(create.fulfilled, (state, action) => {
          state.loading = false;
          state.items.unshift(action.payload);
        })
        .addCase(create.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        })
        // Update
        .addCase(update.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(update.fulfilled, (state, action) => {
          state.loading = false;
          const index = state.items.findIndex(item => item._id === action.payload._id);
          if (index !== -1) {
            state.items[index] = action.payload;
          }
          if (state.currentItem?._id === action.payload._id) {
            state.currentItem = action.payload;
          }
        })
        .addCase(update.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        })
        // Delete
        .addCase(remove.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(remove.fulfilled, (state, action) => {
          state.loading = false;
          state.items = state.items.filter(item => item._id !== action.payload);
          if (state.currentItem?._id === action.payload) {
            state.currentItem = null;
          }
        })
        .addCase(remove.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        });
    },
  });

  return {
    reducer: slice.reducer,
    actions: {
      ...slice.actions,
      fetchAll,
      fetchById,
      create,
      update,
      remove,
    },
  };
};
